/**
 * Client-side helpers for GET /api/lessons/[id]/proactive-status.
 * Kept separate from server queue code so student bundles stay lean.
 */

import { apiFetch } from "@/src/lib/api-fetch";
import type {
  ProactiveGenerationStatusPayload,
  ProactiveGenerationUiStatus,
} from "@/lib/proactive-generation-status-types";

/** Returned with HTTP 503 when the proactive-status route hits an unexpected error. */
export const PROACTIVE_STATUS_UNAVAILABLE_CODE =
  "PROACTIVE_STATUS_UNAVAILABLE" as const;

export type { ProactiveGenerationStatusPayload, ProactiveGenerationUiStatus };

/** Same shape as GET /api/lessons/[id]/proactive-status (alias for clarity in student UI code). */
export type ProactiveStatusPayload = ProactiveGenerationStatusPayload;

const KNOWN_UI_STATUSES: readonly ProactiveGenerationUiStatus[] = [
  "idle",
  "pending",
  "generating",
  "completed",
  "failed",
];

function coerceProactiveUiStatus(raw: string): ProactiveGenerationUiStatus {
  return (KNOWN_UI_STATUSES as readonly string[]).includes(raw)
    ? (raw as ProactiveGenerationUiStatus)
    : "idle";
}

export type ProactiveOverlayCopy = {
  eyebrow: string;
  title: string;
  description: string;
  showSpinner: boolean;
  variant: "info" | "error" | "success";
  statusLabel: string;
  progressHint: string;
};

/** Course preparing poll: interval (see useProactiveCoursePreparingPoll). */
export const PREPARING_POLL_INTERVAL_MS = 10_000;
/**
 * After this long still preparing, switch copy to the calmer "taking longer than
 * usual — feel free to leave" message. Real-course p90 generation is ~145s; 4 min
 * gives comfortable headroom before we soften expectations. Polling continues.
 */
export const PREPARING_ESCALATE_AFTER_MS = 4 * 60 * 1000;
/** Hard stop for the course preparing poll, after which we show a terminal state. */
export const PREPARING_POLL_MAX_MS = 20 * 60 * 1000;

export const LESSON_PROACTIVE_POLL_INITIAL_MS = 3000;
export const LESSON_PROACTIVE_POLL_STEADY_MS = 5000;
export const LESSON_PROACTIVE_POLL_FAST_ROUNDS = 10;
/** Lesson player: stop auto-polling after this; user can refresh manually. */
export const LESSON_PROACTIVE_POLL_MAX_MS = 20 * 60 * 1000;

export type ProactiveFetchResult =
  | { ok: true; outcome: "ready" | "failed" | "pending"; payload: ProactiveStatusPayload }
  | { ok: false; error: string; code?: string };

export function normalizeProactiveStatusBody(
  raw: unknown,
): ProactiveGenerationStatusPayload {
  const st = raw as Record<string, unknown>;
  const rawStatus =
    typeof st.generation_task_status === "string" ? st.generation_task_status : "idle";
  return {
    generation_task_status: coerceProactiveUiStatus(rawStatus),
    has_personalized_content: !!st.has_personalized_content,
    dispatch_lock_active: !!st.dispatch_lock_active,
    proactive_lesson_uuid:
      typeof st.proactive_lesson_uuid === "string" ? st.proactive_lesson_uuid : null,
  };
}

export function interpretProactiveFetchOutcome(
  payload: ProactiveStatusPayload,
): "ready" | "failed" | "pending" {
  // Only treat as ready when content is actually available.
  if (payload.has_personalized_content) {
    return "ready";
  }
  // "completed" without content is inconsistent and should not be treated as ready.
  if (payload.generation_task_status === "completed") {
    return "failed";
  }
  if (
    payload.generation_task_status === "failed" &&
    !payload.dispatch_lock_active
  ) {
    return "failed";
  }
  return "pending";
}

export async function fetchProactiveLessonStatus(
  canonicalLessonId: number,
): Promise<ProactiveFetchResult> {
  const stRes = await apiFetch(`/api/lessons/${canonicalLessonId}/proactive-status`);
  const json = (await stRes.json().catch(() => ({}))) as {
    error?: string;
    code?: string;
  };
  if (!stRes.ok) {
    return {
      ok: false,
      error:
        typeof json.error === "string"
          ? json.error
          : `Status check failed (${stRes.status})`,
      code: typeof json.code === "string" ? json.code : undefined,
    };
  }
  const payload = normalizeProactiveStatusBody(json);
  return {
    ok: true,
    outcome: interpretProactiveFetchOutcome(payload),
    payload,
  };
}

/** Maps API status to course-page full-screen overlay copy. */
export function proactiveStatusToOverlayCopy(
  st: ProactiveStatusPayload | null,
  httpError: string | null,
): ProactiveOverlayCopy {
  if (httpError) {
    return {
      eyebrow: "Personalizing your path",
      title: "Couldn’t load status",
      description: httpError,
      showSpinner: false,
      variant: "error",
      statusLabel: "error",
      progressHint: "Check your connection, then try again.",
    };
  }
  if (!st) {
    return {
      eyebrow: "Personalizing your path",
      title: "Preparing your first lesson",
      description: "Connecting to your lesson workspace and fetching live status…",
      showSpinner: true,
      variant: "info",
      statusLabel: "starting",
      progressHint: "This updates automatically.",
    };
  }
  const raw = st.generation_task_status;
  if (st.has_personalized_content) {
    return {
      eyebrow: "Personalizing your path",
      title: "Your lesson is ready",
      description: "Your personalized content is available. Taking you there now.",
      showSpinner: true,
      variant: "success",
      statusLabel: "completed",
      progressHint: "Almost there…",
    };
  }
  if (raw === "completed") {
    return {
      eyebrow: "Personalization",
      title: "Lesson output missing",
      description:
        "The worker finished but lesson content was not available yet. Try again to queue another run.",
      showSpinner: false,
      variant: "error",
      statusLabel: "completed_no_content",
      progressHint: "Use Try again to request a fresh generation.",
    };
  }
  if (raw === "failed") {
    return {
      eyebrow: "Personalization",
      title: "Generation didn’t finish",
      description:
        "The system reported that this lesson could not be personalized. You can try again — if it keeps failing, contact support.",
      showSpinner: false,
      variant: "error",
      statusLabel: "failed",
      progressHint: "Use Try again to queue another run.",
    };
  }
  if (raw === "pending") {
    return {
      eyebrow: "Personalizing your path",
      title: "In the queue",
      description:
        "Your lesson is waiting to be generated. The worker should pick it up shortly.",
      showSpinner: true,
      variant: "info",
      statusLabel: "pending",
      progressHint: "Queued — usually starts within a few seconds.",
    };
  }
  if (raw === "generating" || (raw === "idle" && st.dispatch_lock_active)) {
    return {
      eyebrow: "Personalizing your path",
      title: "Generating your lesson",
      description:
        "We’re actively building your personalized lesson from your domain and goal. You’ll move forward automatically when it’s ready.",
      showSpinner: true,
      variant: "info",
      statusLabel: "generating",
      progressHint: "Usually takes under a minute.",
    };
  }
  return {
    eyebrow: "Personalizing your path",
    title: "Starting personalization",
    description:
      "Queuing your lesson or waiting for the next update from the server. This page checks status periodically while you wait.",
    showSpinner: true,
    variant: "info",
    statusLabel: "idle",
    progressHint: "If nothing changes for several minutes, try Try again.",
  };
}

export type LessonPersonalizePhase =
  | "idle"
  | "personalizing"
  | "polling"
  | "polling_timeout"
  | "done"
  | "error";

/** Bold headline for the inline lesson banner (aligned with proactiveStatusToOverlayCopy titles). */
export function lessonPersonalizingHeadline(
  phase: LessonPersonalizePhase,
  lastPayload: ProactiveStatusPayload | null,
): string | null {
  if (phase === "polling_timeout") {
    return "Still preparing your lesson";
  }
  if (phase === "personalizing" || phase === "polling") {
    if (phase === "polling" && lastPayload) {
      return proactiveStatusToOverlayCopy(lastPayload, null).title;
    }
    return "Personalizing…";
  }
  return null;
}
